<!-- src/components/SubHeader.vue -->
<template>
  <div class="bg-white relative z-40 border-b border-slate-100/50">
    <div class="max-w-7xl mx-auto px-4 sm:px-0 py-4">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        
        <!-- Left Side: Page Context & Logo -->
        <div class="flex items-center gap-6">
          <div v-if="clientLogo" class="hidden sm:block">
            <img :src="clientLogo" class="h-12 w-auto object-contain" alt="Client Logo">
          </div>
          <div class="h-8 w-px bg-gray-200 hidden sm:block"></div>
          <div>
            <!-- Dynamic Welcome Greeting -->
            <h2 class="text-sm font-black text-[#0A1630] uppercase tracking-[0.2em] flex items-center gap-2">
               {{ displayTitle }}
            </h2>
          </div>
        </div>

        <!-- Right Side: Rail Data & Clock -->
        <div class="flex flex-row items-center justify-between w-full md:w-auto md:justify-end gap-4 md:gap-8">
          
          <div class="text-left md:text-right flex flex-col justify-center h-10">
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-tighter leading-none mb-1">{{ currentDate }}</p>
            <p class="text-xs font-bold text-slate-700 tabular-nums leading-none">{{ currentTime }}</p>
          </div>
          
          <div class="h-10 w-px bg-gray-100 hidden md:block"></div>

          <!-- RESTORED: Rail Week Block -->
          <div @click="openCalendarModal" class="cursor-pointer bg-[#0A1630] px-4 py-1.5 rounded-xl shadow-lg border border-slate-800 transition-all hover:scale-105 hover:border-[#20FCF1]/30 flex flex-col items-center justify-center min-w-[100px] h-10 select-none">
            <p class="text-[8px] font-black text-white uppercase tracking-[0.25em] leading-none mb-1 text-center">Rail Week</p>
            <p class="text-base font-black text-[#FD5E00] leading-none tabular-nums text-center w-full">{{ railWeek }}</p>
          </div>
        </div>
        
      </div>
    </div>
  </div>

  <!-- 🛡️ INTERACTIVE RAIL WEEK CALENDAR POP-UP MODAL -->
  <transition name="scale-in">
    <div v-if="showCalendarModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-8 no-print">
      <!-- Backdrop blur overlay -->
      <div @click="showCalendarModal = false" class="absolute inset-0 bg-[#0A1630]/95 backdrop-blur-xl"></div>
      
      <!-- Modal Content Card -->
      <div class="relative bg-white w-full max-w-2xl rounded-[40px] shadow-[0_30px_70px_rgba(0,0,0,0.5)] border border-slate-200/60 overflow-hidden p-8 sm:p-10 animate-scaleIn flex flex-col gap-6 text-left">
        
        <!-- Header Row -->
        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
          <div class="flex items-center gap-3">
            <CalendarDaysIcon class="h-6 w-6 text-[#20FCF1]" />
            <div>
              <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-wider">Rail Weeks Calendar</h3>
              <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Saturday to Friday POSSESSION PLANNING LAYOUT</p>
            </div>
          </div>
          
          <button @click="showCalendarModal = false" class="text-slate-400 hover:text-[#EF4444] transition-colors p-1 bg-transparent border-none">
            <XMarkIcon class="h-7 w-7" />
          </button>
        </div>

        <!-- Month Navigation Control -->
        <div class="flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100">
          <button @click="navigateMonth(-1)" class="p-2 text-[#0A1630] hover:text-[#20FCF1] transition-colors bg-white rounded-xl border border-slate-200/60 shadow-sm active:scale-90">
            <ChevronLeftIcon class="h-5 w-5" />
          </button>
          <span class="text-sm font-black text-[#0A1630] uppercase tracking-widest select-none">
            {{ monthLabel }} {{ currentCalendarYear }}
          </span>
          <button @click="navigateMonth(1)" class="p-2 text-[#0A1630] hover:text-[#20FCF1] transition-colors bg-white rounded-xl border border-slate-200/60 shadow-sm active:scale-90">
            <ChevronRightIcon class="h-5 w-5" />
          </button>
        </div>

        <!-- Calendar Grid Structure -->
        <div class="overflow-hidden border border-slate-100 rounded-3xl bg-slate-50/50 p-4">
          <!-- Column Headers -->
          <div class="grid grid-cols-8 gap-2 text-center text-[10px] font-black uppercase tracking-wider border-b border-slate-100 pb-3 mb-3 select-none">
            <div class="text-[#20FCF1] font-black">Wk</div>
            <div class="text-slate-500">Sat</div>
            <div class="text-slate-500">Sun</div>
            <div class="text-slate-500">Mon</div>
            <div class="text-slate-500">Tue</div>
            <div class="text-slate-500">Wed</div>
            <div class="text-slate-500">Thu</div>
            <div class="text-slate-500">Fri</div>
          </div>

          <!-- Calendar Rows -->
          <div class="space-y-2">
            <div v-for="(week, wIdx) in calendarWeeks" :key="wIdx" class="grid grid-cols-8 gap-2 items-center text-center">
              <!-- Week Row Number (Blue accent) -->
              <div class="text-[11px] font-black text-[#20FCF1] bg-[#0A1630]/5 py-2.5 rounded-xl border border-[#20FCF1]/15 select-none">
                {{ String(week.weekNum).padStart(2, '0') }}
              </div>

              <!-- Days 1 to 7 -->
              <div v-for="(day, dIdx) in week.days" :key="dIdx" class="relative group">
                <button 
                  v-if="day.isCurrentMonth"
                  @click="selectDay(day)"
                  :class="[
                    day.isToday 
                      ? 'bg-[#FD5E00] text-white shadow-md shadow-[#FD5E00]/20' 
                      : 'bg-white hover:border-[#20FCF1] text-slate-700 hover:text-[#0A1630]',
                    calendarEntries[day.dateString] ? 'border-b-2 border-b-[#FD5E00]' : ''
                  ]"
                  class="w-full py-2.5 rounded-xl text-xs font-black transition-all border border-slate-200/50 flex flex-col items-center justify-center min-h-[38px]"
                >
                  <span>{{ day.dayNum }}</span>
                  <!-- Small dot indicating an entry -->
                  <span v-if="calendarEntries[day.dateString]" class="absolute bottom-1 w-1 h-1 rounded-full" :class="day.isToday ? 'bg-white' : 'bg-[#FD5E00]'"></span>
                </button>
                <div v-else class="min-h-[38px]"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Inline Entry Manager Panel -->
        <transition name="fade-slide">
          <div v-if="showEntryInput" class="bg-slate-50 border border-slate-100 p-6 rounded-3xl space-y-4">
            <div class="flex items-center justify-between">
              <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Calendar Entry for: {{ formattedSelectedDate }}
              </span>
              <button @click="showEntryInput = false" class="text-[9px] font-black text-slate-400 hover:text-slate-600 uppercase tracking-widest border-none bg-transparent">
                Cancel
              </button>
            </div>
            <div class="flex gap-4">
              <input 
                v-model="entryText" 
                type="text" 
                placeholder="Enter possession, schedule, or milestone note..." 
                class="flex-grow px-5 py-3.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-[#0A1630] focus:ring-2 focus:ring-[#20FCF1] outline-none"
                @keyup.enter="saveEntry"
              />
              <button @click="saveEntry" class="px-6 py-3.5 bg-[#0A1630] text-white hover:bg-slate-800 transition-colors rounded-xl text-[10px] font-black uppercase tracking-widest border-none shrink-0 shadow-md">
                Save Note
              </button>
            </div>
          </div>
        </transition>

        <!-- Static Guideline Footer -->
        <p class="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] leading-normal border-t border-slate-100 pt-4 text-center">
          Note: Rail Week 1 typically commences on April 1st. Today's date is highlighted in orange.
        </p>

      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { 
  XMarkIcon, ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon 
} from '@heroicons/vue/24/outline';

const route = useRoute();
const userStore = useUserStore();

const currentTime = ref('');
const currentDate = ref('');
const railWeek = ref('00');

// Dynamic Month-by-Month State for Popup Calendar
const showCalendarModal = ref(false);
const currentCalendarMonth = ref(new Date().getMonth());
const currentCalendarYear = ref(new Date().getFullYear());
const calendarEntries = ref({});

const selectedDate = ref('');
const entryText = ref('');
const showEntryInput = ref(false);

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const monthLabel = computed(() => monthNames[currentCalendarMonth.value]);

const clientLogo = computed(() => userStore.clientProfile?.clientLogoUrl || '');

const clientName = computed(() => {
  return (userStore.userProfile?.clientName || userStore.clientProfile?.name || 'TECS').trim();
});

/**
 * 🛡️ GREETING LOGIC: Replaces 'DASHBOARD' and 'CLIENT ADMINISTRATION' with dynamic tenant command centre greetings [1]
 */
const displayTitle = computed(() => {
  const title = route.meta?.title || 'Sentinel Safe';
  const upperTitle = title.toUpperCase().trim();
  
  if (upperTitle === 'DASHBOARD' || upperTitle === 'CLIENT ADMINISTRATION' || upperTitle === 'CLIENTADMINISTRATION') {
    return `Welcome to the ${clientName.value} Command Centre`;
  }
  return title;
});

const updateClock = () => {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  currentDate.value = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const calculateRailWeek = () => {
  const now = new Date();
  const getStartSaturday = (year) => {
    let target = new Date(year, 2, 29); // March 29th
    while (target.getDay() !== 6) target.setDate(target.getDate() + 1);
    target.setHours(0, 0, 0, 0);
    return target;
  };
  const currentYear = now.getFullYear();
  let railYearStart = getStartSaturday(currentYear);
  if (now < railYearStart) railYearStart = getStartSaturday(currentYear - 1);
  const diffInMs = now - railYearStart;
  const diffInWeeks = Math.floor(diffInMs / (7 * 24 * 60 * 60 * 1000));
  railWeek.value = (diffInWeeks + 1).toString().padStart(2, '0');
};

// 🛡️ DYNAMIC CALENDAR GENERATION WITH Saturday-to-Friday possession layout
const getRailWeekForDate = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const getStartSaturday = (year) => {
    let target = new Date(year, 2, 29);
    while (target.getDay() !== 6) target.setDate(target.getDate() + 1);
    target.setHours(0, 0, 0, 0);
    return target;
  };
  const currentYear = d.getFullYear();
  let railYearStart = getStartSaturday(currentYear);
  if (d < railYearStart) railYearStart = getStartSaturday(currentYear - 1);
  const diffInMs = d - railYearStart;
  const diffInWeeks = Math.floor(diffInMs / (7 * 24 * 60 * 60 * 1000));
  return diffInWeeks + 1;
};

const calendarWeeks = computed(() => {
  const year = currentCalendarYear.value;
  const month = currentCalendarMonth.value;
  
  const firstDayOfMonth = new Date(year, month, 1);
  
  // Backtrack until we find the Saturday (Day 6) of the first week of the month
  let current = new Date(firstDayOfMonth);
  while (current.getDay() !== 6) {
    current.setDate(current.getDate() - 1);
  }
  
  const weeks = [];
  const lastDayOfMonth = new Date(year, month + 1, 0);
  
  while (current <= lastDayOfMonth || current.getMonth() === month || weeks.length < 5) {
    if (current > lastDayOfMonth && current.getMonth() !== month) {
      break;
    }
    
    const weekNum = getRailWeekForDate(current);
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(current);
      dayDate.setDate(current.getDate() + i);
      
      const dateString = formatDateKey(dayDate);
      days.push({
        date: dayDate,
        dayNum: dayDate.getDate(),
        isCurrentMonth: dayDate.getMonth() === month,
        isToday: isSameDay(dayDate, new Date()),
        dateString
      });
    }
    
    weeks.push({
      weekNum,
      days
    });
    
    current.setDate(current.getDate() + 7);
  }
  
  return weeks;
});

const isSameDay = (d1, d2) => {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

const formatDateKey = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const loadEntries = () => {
  try {
    const data = localStorage.getItem('sentinel_rail_calendar');
    calendarEntries.value = data ? JSON.parse(data) : {};
  } catch (err) {
    console.warn("Failed to load local calendar entries:", err);
  }
};

const selectDay = (day) => {
  selectedDate.value = day.dateString;
  entryText.value = calendarEntries.value[day.dateString] || '';
  showEntryInput.value = true;
};

const saveEntry = () => {
  if (!selectedDate.value) return;
  if (entryText.value.trim() === '') {
    delete calendarEntries.value[selectedDate.value];
  } else {
    calendarEntries.value[selectedDate.value] = entryText.value.trim();
  }
  
  try {
    localStorage.setItem('sentinel_rail_calendar', JSON.stringify(calendarEntries.value));
  } catch (err) {
    console.warn("Failed to save local calendar entry:", err);
  }
  showEntryInput.value = false;
};

const formattedSelectedDate = computed(() => {
  if (!selectedDate.value) return '';
  const [year, month, day] = selectedDate.value.split('-');
  const dateObj = new Date(year, month - 1, day);
  return dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
});

const navigateMonth = (direction) => {
  let newMonth = currentCalendarMonth.value + direction;
  let newYear = currentCalendarYear.value;
  if (newMonth < 0) {
    newMonth = 11;
    newYear -= 1;
  } else if (newMonth > 11) {
    newMonth = 0;
    newYear += 1;
  }
  currentCalendarMonth.value = newMonth;
  currentCalendarYear.value = newYear;
};

const openCalendarModal = () => {
  loadEntries();
  const now = new Date();
  currentCalendarMonth.value = now.getMonth();
  currentCalendarYear.value = now.getFullYear();
  showEntryInput.value = false;
  showCalendarModal.value = true;
};

let timer;
onMounted(() => {
  updateClock();
  calculateRailWeek();
  timer = setInterval(() => {
    updateClock();
    const now = new Date();
    if (now.getHours() === 0 && now.getMinutes() === 0 && now.getSeconds() === 0) calculateRailWeek();
  }, 1000);
});

onUnmounted(() => { if (timer) clearInterval(timer); });
</script>

<style scoped>
.scale-in-enter-active, .scale-in-leave-active { transition: opacity 0.3s ease; }
.scale-in-enter-from, .scale-in-leave-to { opacity: 0; }
.animate-scaleIn { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes scaleIn { 
  from { transform: scale(0.97) translateY(15px); opacity: 0; } 
  to { transform: scale(1) translateY(0); opacity: 1; } 
}
.fade-slide-enter-active, .fade-slide-leave-active { 
  transition: all 0.3s ease; 
}
.fade-slide-enter-from, .fade-slide-leave-to { 
  opacity: 0; transform: translateY(-5px); 
}
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { 
  background: #20FCF1; border-radius: 10px; 
}
</style>